import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { IntegrationServiceId } from '../../common/integrations'
import { useAppSelector } from '../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetMeQuery } from '../../store/user/user'
import { useGetBookingsQuery, useGetSchedulingEventTypesQuery, useGetUserSchedulingEventTypesQuery } from '../../store/scheduling/scheduling'
import { BookingStatus, EventTypeAccess, type SchedulingEventType, type SchedulingFilters } from '../../models/Scheduling'
import { useZocksAuth } from '../../components/ScopedBlock/hooks'
import { type SchedulingTabKey } from './bookingTabs'

export type SchedulingPageStatus =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'connect-calendar' }
  | { kind: 'reconnect-calendar'; serviceId?: IntegrationServiceId }
  | { kind: 'no-account-event-types'; isAdmin: boolean }
  | { kind: 'no-user-page-event-types' }
  | { kind: 'ready' }

export interface SchedulingStatusInputs {
  isLoading: boolean
  isError: boolean
  hasCalendar: boolean
  calendarNeedsReconnect: boolean
  reconnectServiceId?: IntegrationServiceId
  hasActiveAccountEventTypes: boolean
  hasActiveUserPageEventTypes: boolean
  isAdmin: boolean
}

export const resolveSchedulingPageStatus = (inputs: SchedulingStatusInputs): SchedulingPageStatus => {
  if (inputs.isLoading) {
    return { kind: 'loading' }
  }
  if (inputs.isError) {
    return { kind: 'error' }
  }
  if (!inputs.hasCalendar) {
    return { kind: 'connect-calendar' }
  }
  if (inputs.calendarNeedsReconnect) {
    return { kind: 'reconnect-calendar', serviceId: inputs.reconnectServiceId }
  }
  if (!inputs.hasActiveAccountEventTypes) {
    return { kind: 'no-account-event-types', isAdmin: inputs.isAdmin }
  }
  if (!inputs.hasActiveUserPageEventTypes) {
    return { kind: 'no-user-page-event-types' }
  }
  return { kind: 'ready' }
}

export const hasActiveUserPageEventTypes = (activeEventTypes: SchedulingEventType[], userPageEventTypeIds: string[]): boolean => {
  const onUserPage = new Set(userPageEventTypeIds)
  return activeEventTypes.some((eventType) => onUserPage.has(eventType.id))
}

const CALENDAR_SERVICE_IDS = [IntegrationServiceId.GOOGLE_CALENDAR, IntegrationServiceId.OUTLOOK_CALENDAR, IntegrationServiceId.EXCHANGE_CALENDAR]
const INVALID_INTEGRATION_STATE = 'invalid'

export const useSchedulingPageStatus = (
  filters: SchedulingFilters,
): { status: SchedulingPageStatus; refetch: () => void; counts: Record<SchedulingTabKey, number>; eventTypes?: SchedulingEventType[] } => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { hasScope } = useZocksAuth()
  const { data: me, isLoading: isMeLoading, isError: isMeError, refetch: refetchMe } = useGetMeQuery({ accountId: authenticatedUser.accountId })

  // One first-page bookings query per tab status, each respecting the shared filters; the tab
  // counts come from the responses' page.totalElements. The args match a tab's initial
  // (first-page, default-size) query, so that fetch and its count share one request/cache
  // entry; tabs paginated past page one fetch their own entries.
  const bookingsArgs = (status: BookingStatus) => ({
    ...filters,
    accountId: authenticatedUser.accountId,
    status,
    pageRequest: { page: 0, size: DEFAULT_PAGE_SIZE },
  })
  // Wait for the owner filter (seeded with the current user) so we never fire an unfiltered request.
  const skipBookings = { skip: !filters.userIds?.length }
  const requested = useGetBookingsQuery(bookingsArgs(BookingStatus.REQUESTED), skipBookings)
  const suggested = useGetBookingsQuery(bookingsArgs(BookingStatus.OPPORTUNITY), skipBookings)
  const waiting = useGetBookingsQuery(bookingsArgs(BookingStatus.AWAITING_REPLY), skipBookings)
  const scheduled = useGetBookingsQuery(bookingsArgs(BookingStatus.CONFIRMED), skipBookings)
  const bookingQueries = [requested, suggested, waiting, scheduled]

  const counts: Record<SchedulingTabKey, number> = {
    requests: requested.data?.page.totalElements ?? 0,
    suggested: suggested.data?.page.totalElements ?? 0,
    waiting: waiting.data?.page.totalElements ?? 0,
    scheduled: scheduled.data?.page.totalElements ?? 0,
  }
  const {
    data: eventTypes,
    isLoading: isEventTypesLoading,
    isError: isEventTypesError,
    refetch: refetchEventTypes,
  } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const {
    data: userEventTypeIds,
    isLoading: isUserEventTypesLoading,
    isError: isUserEventTypesError,
    refetch: refetchUserEventTypes,
  } = useGetUserSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId, userId: authenticatedUser.id })

  const calendarIntegration = (me?.integrations ?? []).find((integration) => CALENDAR_SERVICE_IDS.includes(integration.serviceId))
  const activeEventTypes = (eventTypes ?? []).filter((eventType) => eventType.active)

  const status = resolveSchedulingPageStatus({
    isLoading: isMeLoading || bookingQueries.some((query) => query.isLoading) || isEventTypesLoading || isUserEventTypesLoading,
    isError: isMeError || bookingQueries.some((query) => query.isError) || isEventTypesError || isUserEventTypesError,
    hasCalendar: Boolean(calendarIntegration),
    calendarNeedsReconnect: calendarIntegration?.state === INVALID_INTEGRATION_STATE,
    reconnectServiceId: calendarIntegration?.serviceId,
    hasActiveAccountEventTypes: activeEventTypes.some((eventType) => eventType.access === EventTypeAccess.ACCOUNT),
    hasActiveUserPageEventTypes: hasActiveUserPageEventTypes(activeEventTypes, userEventTypeIds ?? []),
    isAdmin: hasScope('**', 'account_config:update', ['account_config:admin']),
  })

  const refetch = () => {
    refetchMe()
    requested.refetch()
    suggested.refetch()
    waiting.refetch()
    scheduled.refetch()
    refetchEventTypes()
    refetchUserEventTypes()
  }

  return { status, refetch, counts, eventTypes }
}
